import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FaSearch, FaPaperPlane, FaArrowLeft, FaComment, FaHeart,
  FaSpinner, FaCheck, FaCheckDouble
} from 'react-icons/fa';
import axios from 'axios';
import { message } from 'antd';
import { BASE_API } from "../../Api/Api";
import './Messages.css';

const Messages = () => {
  const [selectedChat, setSelectedChat] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [chatList, setChatList] = useState([]);
  const [chatMessages, setChatMessages] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [wsConnected, setWsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});

  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const heartbeatRef = useRef(null);
  const reconnectRef = useRef(null);
  const isUnmountingRef = useRef(false);
  const reconnectCountRef = useRef(0);
  const pollingRef = useRef(null);
  const onlinePollRef = useRef(null);
  const selectedChatRef = useRef(null);

  const userId = localStorage.getItem('user_id') || '';
  const BASE_URL = BASE_API.replace(/\/+$/, '');

  const authHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getWsBaseUrl = () =>
    BASE_URL.replace('https://', 'wss://').replace('http://', 'ws://');

  // ── helpers ──────────────────────────────────────────────
  const pickName = (userData, userInfo, req, otherUserId) => {
    return (
      userData?.firstname ||
      userData?.name ||
      userData?.fullname ||
      userData?.username ||
      userInfo?.first_name ||
      userInfo?.username ||
      userInfo?.name ||
      req?.from_user_name ||
      req?.to_user_name ||
      req?.other_user_name ||
      req?.name ||
      (otherUserId ? `User ${String(otherUserId).slice(0, 6)}` : 'User')
    );
  };

  const pickOnline = (userData, userInfo) => {
    const v =
      userData?.is_online ??
      userData?.online ??
      userInfo?.is_online ??
      userInfo?.online ??
      false;
    return v === true || v === 1 || v === 'true' || v === 'online';
  };

  const stopHeartbeat = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  const startHeartbeat = (ws) => {
    stopHeartbeat();
    heartbeatRef.current = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 20000);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const stopOnlinePoll = () => {
    if (onlinePollRef.current) {
      clearInterval(onlinePollRef.current);
      onlinePollRef.current = null;
    }
  };

  // ── WS send ──────────────────────────────────────────────
  const sendWebSocketMessage = (type, data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, ...data }));
      return true;
    }
    return false;
  };

    const triggerReadReceipt = async (chatUserId) => {
    if (!chatUserId) return;
    
    const me = localStorage.getItem('user_id');
    const token = localStorage.getItem('access_token');

    // 1. Instant WebSocket Read Receipt
    sendWebSocketMessage('read_receipt', {
      sender_id: chatUserId,
      receiver_id: me,
    });

    if (!token) return;

    // 2. HTTP Call to Backend - FIX: mark-read endpoint use karein
    try {
      await axios.post(
        `${BASE_URL}/api/chats/${chatUserId}/mark-read/`, 
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
    } catch (err) {
      if (err.response?.status === 403) {
        console.warn('⚠️ Token expired or invalid authorization');
      }
    }
  };

  // ── Online status refresh from UserData API ──────────────
  const refreshOnlineStatuses = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/UserData/`, { headers: authHeaders() });
      const allUD = Array.isArray(res.data) ? res.data : res.data?.results || [];

      setChatList((prev) =>
        prev.map((c) => {
          const ud = allUD.find(
            (u) =>
              String(u.user_id ?? u.user ?? u.User_id ?? u.id) === String(c.id)
          );
          if (!ud) return c;
          const online = pickOnline(ud, null);
          // name bhi refresh kar do agar pehle "User" tha
          const betterName =
            ud.firstname || ud.name || ud.fullname || ud.username || c.name;
          const betterImage = ud.pic
            ? ud.pic.startsWith('http')
              ? ud.pic
              : `${BASE_URL}${ud.pic}`
            : c.image;
          const betterProfile =
            ud.profile_id || c.profile_id || c.bid;

          return {
            ...c,
            online,
            name: betterName,
            image: betterImage,
            profile_id: betterProfile,
          };
        })
      );
    } catch {
      /* silent */
    }
  }, [BASE_URL]);

  // ── WebSocket ────────────────────────────────────────────
  const connectWebSocket = useCallback(() => {
    if (isUnmountingRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (wsRef.current?.readyState === WebSocket.CONNECTING) return;

    const currentToken = localStorage.getItem('access_token');
    const currentUserId = localStorage.getItem('user_id');
    if (!currentToken || !currentUserId) return;

    const wsUrl = `${getWsBaseUrl()}/ws/chat/${currentUserId}/?token=${currentToken}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch {
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      reconnectCountRef.current = 0;
      startHeartbeat(ws);
      stopPolling();
      // apna online status backend ko batao
      ws.send(JSON.stringify({ type: 'user_online', user_id: currentUserId }));
      if (selectedChatRef.current) triggerReadReceipt(selectedChatRef.current);
      // statuses refresh
      refreshOnlineStatuses();
    };

    ws.onclose = (event) => {
      setWsConnected(false);
      stopHeartbeat();
      if (selectedChatRef.current) startPolling(selectedChatRef.current);
      if (isUnmountingRef.current || event.code === 1000) return;
      if (reconnectCountRef.current >= 5) return;
      const delay = Math.min(3000 * (reconnectCountRef.current + 1), 15000);
      reconnectRef.current = setTimeout(() => {
        if (!isUnmountingRef.current) {
          reconnectCountRef.current += 1;
          connectWebSocket();
        }
      }, delay);
    };

    ws.onerror = () => setWsConnected(false);

    ws.onmessage = (event) => {
      try {
        handleWebSocketMessage(JSON.parse(event.data));
      } catch {}
    };
  }, [refreshOnlineStatuses]);

  const startPolling = useCallback((chatId) => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      if (selectedChatRef.current) {
        await fetchChatMessagesSilent(selectedChatRef.current);
      }
    }, 3000);
  }, []);

  // ── WS message handler ───────────────────────────────────
  const handleWebSocketMessage = (data) => {
    const me = String(localStorage.getItem('user_id') || '');

    switch (data.type) {
      case 'pong':
      case 'connection_success':
        break;

      case 'user_status':
      case 'presence':
      case 'online_status': {
        // backend alag keys bhej sakta hai
        const targetUserId = String(
          data.user_id || data.user || data.id || data.sender_id || ''
        );
        const statusRaw = data.status ?? data.is_online ?? data.online;
        const isOnline =
          statusRaw === true ||
          statusRaw === 1 ||
          statusRaw === 'true' ||
          statusRaw === 'online';

        if (!targetUserId) break;

        setChatList((prev) =>
          prev.map((c) =>
            String(c.id) === targetUserId ? { ...c, online: isOnline } : c
          )
        );
        break;
      }

      case 'message': {
        const senderId = String(data.sender_id || '');
        const chatId =
          senderId === me ? String(data.receiver_id) : senderId;
        const isViewing = String(selectedChatRef.current) === String(chatId);

        // jo message bhej raha hai woh online maano
        if (senderId && senderId !== me) {
          setChatList((prev) =>
            prev.map((c) =>
              String(c.id) === senderId ? { ...c, online: true } : c
            )
          );
        }

        if (isViewing && senderId !== me) triggerReadReceipt(chatId);

        setChatMessages((prev) => {
          const existing = prev[chatId] || [];
          if (existing.some((m) => String(m.id) === String(data.message_id)))
            return prev;
          return {
            ...prev,
            [chatId]: [
              ...existing,
              {
                id: data.message_id,
                sender: senderId,
                text: data.message,
                time: new Date(data.timestamp || Date.now()).toLocaleTimeString(
                  'en-IN',
                  { hour: '2-digit', minute: '2-digit' }
                ),
                read: isViewing,
                delivered: true,
                created_at: data.timestamp,
              },
            ],
          };
        });

        setChatList((prev) =>
          prev.map((c) =>
            String(c.id) === String(chatId)
              ? {
                  ...c,
                  lastMessage: data.message,
                  lastMessageTime: 'Just now',
                  unreadCount: isViewing ? 0 : (c.unreadCount || 0) + 1,
                }
              : c
          )
        );
        break;
      }

      case 'message_sent': {
        const receiverId = String(data.receiver_id || '');
        setChatMessages((prev) => {
          const msgs = prev[receiverId] || [];
          return {
            ...prev,
            [receiverId]: msgs.map((m) =>
              m.sending && m.text === data.message
                ? { ...m, id: data.message_id, sending: false, delivered: true }
                : m
            ),
          };
        });
        break;
      }

      case 'read_receipt': {
        const readerId = String(data.reader_id || data.sender_id || '');
        setChatMessages((prev) => {
          const msgs = prev[readerId] || [];
          return {
            ...prev,
            [readerId]: msgs.map((m) =>
              String(m.sender) === me
                ? { ...m, read: true, delivered: true }
                : m
            ),
          };
        });
        break;
      }

      case 'typing': {
        const sid = String(data.sender_id || '');
        setTypingUsers((prev) => ({ ...prev, [sid]: !!data.is_typing }));
        // typing = online
        if (sid) {
          setChatList((prev) =>
            prev.map((c) =>
              String(c.id) === sid ? { ...c, online: true } : c
            )
          );
        }
        break;
      }

      case 'token_expired':
        message.error('Session expired. Please login again.');
        break;

      default:
        break;
    }
  };

  // ── mount ────────────────────────────────────────────────
  useEffect(() => {
    isUnmountingRef.current = false;
    const token = localStorage.getItem('access_token');
    const uid = localStorage.getItem('user_id');

    const t = setTimeout(() => {
      if (uid && token) connectWebSocket();
    }, 300);

    // online status har 20 sec refresh
    onlinePollRef.current = setInterval(() => {
      refreshOnlineStatuses();
    }, 20000);

    return () => {
      isUnmountingRef.current = true;
      clearTimeout(t);
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      stopHeartbeat();
      stopPolling();
      stopOnlinePoll();
      if (wsRef.current) {
        // offline batao
        try {
          if (wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(
              JSON.stringify({ type: 'user_offline', user_id: uid })
            );
          }
        } catch {}
        wsRef.current.close(1000, 'Unmounted');
        wsRef.current = null;
      }
    };
  }, []);

  // ── fetch messages ───────────────────────────────────────
  const fetchChatMessages = async (chatUserId) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/chats/${chatUserId}/messages/`,
        { headers: authHeaders() }
      );
      const messages = (res.data || []).map((msg) => ({
        id: msg.id,
        sender: String(msg.sender || ''),
        text: msg.content || '',
        time: new Date(msg.created_at).toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
        }),
        read: !!msg.is_read,
        delivered: true,
        sender_name: msg.sender_name || '',
        created_at: msg.created_at,
      }));
      setChatMessages((prev) => ({ ...prev, [chatUserId]: messages }));
    } catch {}
  };

  const fetchChatMessagesSilent = async (chatUserId) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/chats/${chatUserId}/messages/`,
        { headers: authHeaders() }
      );
      const messages = (res.data || []).map((msg) => ({
        id: msg.id,
        sender: String(msg.sender || ''),
        text: msg.content || '',
        time: new Date(msg.created_at).toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
        }),
        read: !!msg.is_read,
        delivered: true,
        sender_name: msg.sender_name || '',
        created_at: msg.created_at,
      }));
      setChatMessages((prev) => {
        const existing = prev[chatUserId] || [];
        const hasDiff =
          existing.length !== messages.length ||
          existing.some(
            (m, i) => m.id !== messages[i]?.id || m.read !== messages[i]?.read
          );
        if (!hasDiff) return prev;
        return { ...prev, [chatUserId]: messages };
      });
    } catch {}
  };

  // ── FETCH CHAT LIST (NAME + ONLINE FIX) ──────────────────
  const fetchChatList = async () => {
    try {
      setLoading(true);
      const headers = authHeaders();

      const requestsResponse = await axios.get(`${BASE_URL}/requests/`, {
        headers,
      });
      const acceptedRequests = (requestsResponse.data || []).filter(
        (r) => r.status === 'accepted'
      );

      if (acceptedRequests.length === 0) {
        setChatList([]);
        setLoading(false);
        return;
      }

      // ✅ UserData + UserRegister ek baar bulk fetch (sahi await)
      let allUserData = [];
      let allUsers = [];
      try {
        const udRes = await axios.get(`${BASE_URL}/UserData/`, { headers });
        allUserData = Array.isArray(udRes.data)
          ? udRes.data
          : udRes.data?.results || [];
      } catch (e) {
        console.warn('UserData fetch failed', e);
      }
      try {
        const urRes = await axios.get(`${BASE_URL}/UserRegister/`, { headers });
        allUsers = Array.isArray(urRes.data)
          ? urRes.data
          : urRes.data?.results || [];
      } catch (e) {
        console.warn('UserRegister fetch failed', e);
      }

      const findUserData = (otherUserId) =>
        allUserData.find(
          (ud) =>
            String(ud.user_id ?? ud.user ?? ud.User_id ?? '') ===
            String(otherUserId)
        );

      const findUserInfo = (otherUserId) =>
        allUsers.find((u) => String(u.id) === String(otherUserId));

      const chatUsers = [];

      for (const req of acceptedRequests) {
        const otherUserId =
          req.type === 'received' ? req.from_user : req.to_user;
        if (!otherUserId) continue;
        if (chatUsers.some((u) => String(u.id) === String(otherUserId)))
          continue;

        const userData = findUserData(otherUserId);
        const userInfo = findUserInfo(otherUserId);

        let lastMessage = 'Start chatting 💬';
        let lastMessageTime = 'New';
        let unreadCount = 0;

        try {
          const msgRes = await axios.get(
            `${BASE_URL}/api/chats/${otherUserId}/messages/`,
            { headers }
          );
          const msgs = msgRes.data || [];
          if (msgs.length > 0) {
            const last = msgs[msgs.length - 1];
            lastMessage = last.content || '';
            lastMessageTime = new Date(last.created_at).toLocaleTimeString(
              'en-IN',
              { hour: '2-digit', minute: '2-digit' }
            );
            unreadCount = msgs.filter(
              (m) =>
                !m.is_read && String(m.sender) === String(otherUserId)
            ).length;
          }
        } catch {}

        const name = pickName(userData, userInfo, req, otherUserId);
        const online = pickOnline(userData, userInfo);
        const profile_id =
          userData?.profile_id ||
          userInfo?.profile_id ||
          req.profile_id ||
          req.from_profile_id ||
          req.to_profile_id ||
          null;

        let image = null;
        if (userData?.pic) {
          image = String(userData.pic).startsWith('http')
            ? userData.pic
            : `${BASE_URL}${userData.pic}`;
        }

        chatUsers.push({
          id: otherUserId,
          name,
          bid: userData?.user_id || otherUserId,
          profile_id,
          image,
          online,
          lastMessage,
          lastMessageTime,
          unreadCount,
          isTyping: false,
        });
      }

      setChatList(chatUsers);
    } catch (err) {
      console.error(err);
      message.error('Failed to load chats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChatList();
  }, []);

  // ── select chat ──────────────────────────────────────────
  const handleSelectChat = (chatId) => {
    setSelectedChat(chatId);
    selectedChatRef.current = chatId;
    setChatList((prev) =>
      prev.map((c) =>
        String(c.id) === String(chatId) ? { ...c, unreadCount: 0 } : c
      )
    );
    fetchChatMessages(chatId);
    startPolling(chatId);
    setTimeout(() => triggerReadReceipt(chatId), 200);
  };

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, selectedChat]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── send ─────────────────────────────────────────────────
  const sendMessageViaHTTP = async (receiverId, content) => {
    const res = await axios.post(
      `${BASE_URL}/api/chats/${receiverId}/messages/`,
      { content },
      { headers: authHeaders() }
    );
    return res.data;
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedChat) return;
    const me = localStorage.getItem('user_id');
    const msgText = messageInput.trim();
    setMessageInput('');
    setSending(true);
    const tempId = `temp_${Date.now()}`;

    setChatMessages((prev) => ({
      ...prev,
      [selectedChat]: [
        ...(prev[selectedChat] || []),
        {
          id: tempId,
          sender: String(me),
          text: msgText,
          time: new Date().toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
          }),
          read: false,
          delivered: false,
          sending: true,
          created_at: new Date().toISOString(),
        },
      ],
    }));

    try {
      const wsSent = sendWebSocketMessage('message', {
        receiver_id: selectedChat,
        message: msgText,
      });
      if (!wsSent) {
        const saved = await sendMessageViaHTTP(selectedChat, msgText);
        if (saved) {
          setChatMessages((prev) => ({
            ...prev,
            [selectedChat]: (prev[selectedChat] || []).map((m) =>
              m.id === tempId
                ? {
                    id: saved.id,
                    sender: String(saved.sender),
                    text: saved.content,
                    time: new Date(saved.created_at).toLocaleTimeString(
                      'en-IN',
                      { hour: '2-digit', minute: '2-digit' }
                    ),
                    read: false,
                    delivered: true,
                    sending: false,
                    created_at: saved.created_at,
                  }
                : m
            ),
          }));
        }
        setTimeout(() => fetchChatMessagesSilent(selectedChat), 500);
      }
      setChatList((prev) =>
        prev.map((c) =>
          String(c.id) === String(selectedChat)
            ? { ...c, lastMessage: msgText, lastMessageTime: 'Just now' }
            : c
        )
      );
    } catch {
      message.error('Failed to send message');
      setChatMessages((prev) => ({
        ...prev,
        [selectedChat]: (prev[selectedChat] || []).filter(
          (m) => m.id !== tempId
        ),
      }));
    } finally {
      setSending(false);
    }
  };

  const sendTyping = (receiverId, isTyping) =>
    sendWebSocketMessage('typing', {
      receiver_id: receiverId,
      is_typing: isTyping,
    });

  const handleTyping = (e) => {
    setMessageInput(e.target.value);
    if (selectedChat) {
      sendTyping(selectedChat, true);
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(
        () => sendTyping(selectedChat, false),
        2000
      );
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getChatUser = (chatId) =>
    chatList.find((c) => String(c.id) === String(chatId));
  const getMessages = () =>
    selectedChat ? chatMessages[selectedChat] || [] : [];
  const isUserTyping = (chatId) => !!typingUsers[String(chatId)];
  const me = localStorage.getItem('user_id');

  const filteredChats = chatList.filter(
    (chat) =>
      chat.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(chat.bid || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(chat.profile_id || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  return (
    <div className="msg-page">
      <div className="msg-wrap">
        <div className="msg-container">
          {/* SIDEBAR */}
          <div
            className="msg-sidebar"
            style={isMobile ? { display: selectedChat ? 'none' : 'flex' } : {}}
          >
            <div className="msg-sidebar-header">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <h3 className="msg-sidebar-title">
                  <FaComment size={18} /> Messages
                </h3>
                
              </div>
              <div className="msg-search-wrap">
                <FaSearch className="msg-search-icon" />
                <input
                  type="text"
                  placeholder="Search by name or ID..."
                  className="msg-search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="msg-list">
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <FaSpinner
                    size={24}
                    style={{
                      color: '#ff0055',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                  <p style={{ color: '#888', marginTop: 12, fontSize: 13 }}>
                    Loading chats...
                  </p>
                </div>
              ) : filteredChats.length > 0 ? (
                filteredChats.map((chat) => (
                  <div
                    key={chat.id}
                    className={`msg-chat-item ${
                      String(selectedChat) === String(chat.id) ? 'active' : ''
                    }`}
                    onClick={() => handleSelectChat(chat.id)}
                  >
                    <div className="msg-avatar-wrap">
                      <img
                        src={
                          chat.image ||
                          `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            chat.name || 'U'
                          )}&background=ff0055&color=fff&size=128`
                        }
                        alt={chat.name}
                        className="msg-avatar"
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            chat.name || 'U'
                          )}&background=ff0055&color=fff&size=128`;
                        }}
                      />
                      {chat.online && <span className="msg-online-dot" />}
                    </div>

                    <div className="msg-chat-info">
                      <p className="msg-chat-name">{chat.name}</p>
                      <p className="msg-chat-bid">
                        ID: {chat.profile_id || chat.bid || 'N/A'}
                      </p>
                      <p className="msg-chat-last">
                        {isUserTyping(chat.id) ? (
                          <span className="msg-typing-text">✍️ Typing...</span>
                        ) : (
                          chat.lastMessage || 'No messages yet'
                        )}
                      </p>
                    </div>

                    <div className="msg-chat-right">
                      <span className="msg-chat-time">
                        {chat.lastMessageTime}
                      </span>
                      <div className="msg-chat-right-bottom">
                        {chat.unreadCount > 0 && (
                          <span className="msg-unread-badge">
                            {chat.unreadCount}
                          </span>
                        )}
                        <span
                          className={`msg-list-status-dot ${
                            chat.online ? 'online' : 'offline'
                          }`}
                          title={chat.online ? 'Online' : 'Offline'}
                        />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#999',
                  }}
                >
                  <p style={{ fontSize: 40, marginBottom: 12 }}>💬</p>
                  <p>No accepted connections yet</p>
                </div>
              )}
            </div>
          </div>

          {/* CHAT WINDOW */}
          {selectedChat ? (
            <div
              className="msg-chat-window"
              style={
                isMobile
                  ? { display: selectedChat ? 'flex' : 'none' }
                  : {}
              }
            >
              <div className="msg-chat-header">
                <div className="msg-chat-header-left">
                  <button
                    className="msg-back-btn"
                    onClick={() => {
                      setSelectedChat(null);
                      selectedChatRef.current = null;
                      stopPolling();
                    }}
                  >
                    <FaArrowLeft size={14} />
                  </button>
                  <img
                    src={
                      getChatUser(selectedChat)?.image ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        getChatUser(selectedChat)?.name || 'User'
                      )}&background=ff0055&color=fff&size=128`
                    }
                    alt="User"
                    className="msg-chat-header-img"
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        getChatUser(selectedChat)?.name || 'User'
                      )}&background=ff0055&color=fff&size=128`;
                    }}
                  />
                  <div>
                    <p className="msg-chat-header-name">
                      {getChatUser(selectedChat)?.name || 'User'}
                    </p>
                    <p
                      style={{
                        fontSize: 11,
                        color: '#ff4d88',
                        fontWeight: 600,
                        margin: '2px 0',
                      }}
                    >
                      ID:{' '}
                      {getChatUser(selectedChat)?.profile_id ||
                        getChatUser(selectedChat)?.bid ||
                        'N/A'}
                    </p>
                    <p className="msg-chat-header-status">
                      <span
                        className={`msg-status-dot ${
                          getChatUser(selectedChat)?.online
                            ? 'online'
                            : 'offline'
                        }`}
                      />
                      {getChatUser(selectedChat)?.online
                        ? 'Active now'
                        : 'Offline'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="msg-messages-area">
                {getMessages().length > 0 ? (
                  <>
                    <div className="msg-date-divider">
                      <span>💖 Today</span>
                    </div>
                    {getMessages().map((m) => {
                      const isMe = String(m.sender) === String(me);
                      return (
                        <div
                          key={m.id}
                          className={`msg-bubble-wrap ${isMe ? 'me' : 'them'}`}
                        >
                          <div
                            className={`msg-bubble ${isMe ? 'me' : 'them'} ${
                              m.sending ? 'msg-sending' : ''
                            }`}
                          >
                            {m.text}
                            <div className="msg-time-row">
                              {m.time}
                              {isMe &&
                                (m.sending ? (
                                  <FaSpinner
                                    size={9}
                                    style={{
                                      animation: 'spin 1s linear infinite',
                                    }}
                                  />
                                ) : m.read ? (
                                  <span className="msg-tick seen">
                                    <FaCheckDouble size={11} />
                                  </span>
                                ) : m.delivered ? (
                                  <span className="msg-tick delivered">
                                    <FaCheckDouble size={11} />
                                  </span>
                                ) : (
                                  <span className="msg-tick sent">
                                    <FaCheck size={11} />
                                  </span>
                                ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      color: '#999',
                    }}
                  >
                    <p style={{ fontSize: 48, marginBottom: 16 }}>💬</p>
                    <p style={{ fontWeight: 600, color: '#666' }}>
                      No messages yet
                    </p>
                    <p style={{ fontSize: 13, marginTop: 4 }}>Say hello! 👋</p>
                  </div>
                )}

                {isUserTyping(selectedChat) && (
                  <div className="msg-bubble-wrap them">
                    <div className="msg-typing">
                      <span
                        className="msg-typing-dot"
                        style={{ animationDelay: '0s' }}
                      />
                      <span
                        className="msg-typing-dot"
                        style={{ animationDelay: '.2s' }}
                      />
                      <span
                        className="msg-typing-dot"
                        style={{ animationDelay: '.4s' }}
                      />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="msg-input-area">
                <div className="msg-input-wrap">
                  <textarea
                    className="msg-textarea"
                    placeholder="Type a message..."
                    value={messageInput}
                    onChange={handleTyping}
                    onKeyDown={handleKeyPress}
                    rows="1"
                    disabled={sending}
                  />
                </div>
                <button
                  className="msg-send-btn"
                  onClick={handleSendMessage}
                  disabled={sending || !messageInput.trim()}
                >
                  {sending ? (
                    <>
                      <FaSpinner
                        size={14}
                        style={{ animation: 'spin 1s linear infinite' }}
                      />
                      <span className="msg-btn-text">Sending...</span>
                    </>
                  ) : (
                    <>
                      <FaPaperPlane size={14} />
                      <span className="msg-btn-text">Send</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div
              className="msg-chat-window"
              style={
                isMobile
                  ? { display: selectedChat ? 'flex' : 'none' }
                  : {}
              }
            >
              <div className="msg-empty">
                <div className="msg-empty-icon">
                  <FaHeart />
                </div>
                <h3 className="msg-empty-title">No Conversation Selected</h3>
                <p className="msg-empty-sub">
                  Choose a conversation from the list to start chatting 💖
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;